<!--
SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>

SPDX-License-Identifier: MPL-2.0

@component
Transcription page — top-level route component.
Renders a tab bar that switches between:
  - "file" (file upload transcription via FileTranscription)
  - "live" (real-time microphone transcription via LiveTranscription)
Also mounts the Snackbar for error/confirmation toasts and prevents
the browser's default file-drop behaviour outside the upload zone.
-->
<script>
import { fly } from 'svelte/transition';
import uiStore from '../../utils/UIStore';
import { renderMarkdown } from '../../utils/markdownParser';
import FileTranscription from './FileTranscription.svelte';
import LiveTranscription from './LiveTranscription.svelte';
import Snackbar from './Snackbar.svelte';

// Load UI texts from the store and pre-render markdown fields.
let uiTexts;
/** @type {string} Pre-rendered markdown for the file-upload tab description. */
let transcriptionText = '';
/** @type {string} Pre-rendered markdown for the page introduction paragraph. */
let transcriptionPageIntro = '';
/** @type {string} Pre-rendered markdown for the live transcription tab description. */
let transcriptionRealtimeText = '';
uiStore.textStore.subscribe((store) => {
  uiTexts = store.texts;
  transcriptionText = renderMarkdown(uiTexts.transcriptionText);
  transcriptionPageIntro = renderMarkdown(uiTexts.transcriptionPageIntro);
  transcriptionRealtimeText = renderMarkdown(uiTexts.transcriptionRealtimeText);
});

/** @type {'file'|'live'} Currently active tab. */
let activeTab = 'file';

/**
 * Returns true when the drag event carries file data.
 * Used to ignore non-file drags (e.g. text selections).
 * @param {DragEvent} event
 * @returns {boolean}
 */
function shouldHandleFileDrop(event) {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files');
}

/**
 * Prevent the browser from navigating to a dropped file.
 * Only suppresses events that carry actual file data.
 * @param {DragEvent} event
 */
function preventBrowserFileDrop(event) {
  if (!shouldHandleFileDrop(event)) return;
  event.preventDefault();
}
</script>

<svelte:window on:dragover={preventBrowserFileDrop} on:drop={preventBrowserFileDrop} />

<svelte:head>
  <title>{uiTexts.pageTitles.transcription}</title>
</svelte:head>

<!-- ────────── MAIN CONTENT ────────── -->
<main class="transcription-page">
  <Snackbar />
	<section
	in:fly={{ y: -20, duration: 500, delay: 200 }}
	class="page-container"
	id="scrollTo">

        <!-- ── Page intro: explains both options ── -->
        <div class="hinweis">
            <div style="flex:1;padding-right:24px">
                <p style="padding-top:0" class="textlink">
                {@html transcriptionPageIntro}
                </p>
                {#if uiTexts?.warnhinweisTranskription}
                  <div class="halluzination-warning inline-warning transcription-warning">
                    {uiTexts.warnhinweisTranskription}
                  </div>
                {/if}
            </div>
        </div>

        <!-- ── Tab bar ── -->
        <div class="transcription-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'file'}
            class="tab-btn {activeTab === 'file' ? 'active' : ''}"
            on:click={() => (activeTab = 'file')}
          >
            {uiTexts.transcription.tabFile}
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'live'}
            class="tab-btn {activeTab === 'live' ? 'active' : ''}"
            on:click={() => (activeTab = 'live')}
          >
            {uiTexts.transcription.tabLive}
          </button>
        </div>

        <!-- ── Tab panels ── -->
        <div class="tab-panel" role="tabpanel" hidden={activeTab !== 'file'}>
          <FileTranscription descriptionHtml={transcriptionText} />
        </div>
        {#if activeTab === 'live'}
          <div class="tab-panel" role="tabpanel">
            <div class="tab-description">
              <p class="textlink">{@html transcriptionRealtimeText}</p>
            </div>
            <LiveTranscription />
          </div>
        {/if}
	</section>
</main>

<style>
	.transcription-page {
		padding-top: 90px;
		padding-bottom:80px;
	}
	.page-container {
		width: 100%;
		padding-top: 50px;
		max-width: 1200px;
		margin: auto;
		display: flex;
		flex-direction: column;
		height: 100%;
		align-items: center;
	}
    .hinweis {
		display: flex;
		text-align: left;
		margin-bottom: 12px;
	}

	.hinweis p {
		font-size: 1rem;
	}

  .transcription-warning {
    margin-top: 8px;
  }

  /* ── Tab bar ── */
  .transcription-tabs {
    display: flex;
    width: 100%;
    border-bottom: 2px solid #e0e0e0;
    margin-bottom: 28px;
    gap: 0;
  }

  .tab-btn {
    background: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    margin-bottom: -2px;
    padding: 0 28px;
    height: 48px;
    font-size: 1rem;
    font-weight: 600;
    font-family: "MainFont_SemiBold", Arial, Helvetica, sans-serif;
    text-transform: uppercase;
    color: inherit;
    cursor: pointer;
    letter-spacing: 0.02em;
    transition: border-color 0.15s, color 0.15s;
    outline: none;
  }

  .tab-btn:focus {
    outline: none;
    box-shadow: none;
  }

  .tab-btn:hover {
    border-bottom-color: var(--sec-color);
  }

  .tab-btn.active {
    border-bottom-color: var(--sec-color);
    color: inherit;
  }

  .tab-panel {
    width: 100%;
  }

  .tab-description {
    margin-bottom: 20px;
    text-align: left;
    width: 100%;
  }

  .tab-description p {
    font-size: 1rem;
    padding-top: 0;
  }
</style>